import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTask, useTags } from "../../../../../hooks/query";
import moment from "moment";

interface NewSubTaskInlineFormData {
    projectId: string | { value: string; label: string };
    eventName: string;
    startDateTime: string;
    endDateTime: string;
    taskStatus: string;
    description: string;
    assignee: string | { value: string; label: string };
    color: string;
}

interface UseNewSubTaskProps {
    selectedDate: Date;
    onFormStateChange?: (isValid: boolean) => void;
}

export function useNewSubTask({ selectedDate, onFormStateChange }: UseNewSubTaskProps) {
    // Task creation hooks
    const { useCreate, useListByProjectId } = useTask();
    const createTaskSvc = useCreate();
    const listProjectTasksSvc = useListByProjectId();

    // Tags hooks
    const { useAssignTags } = useTags();
    const assignTagsSvc = useAssignTags();

    // State for Job Execution task ID
    const [jobExecutionTaskId, setJobExecutionTaskId] = useState<string | null>(null);

    // Form setup
    const methods = useForm<NewSubTaskInlineFormData>({
        defaultValues: {
            projectId: "",
            eventName: "",
            startDateTime: selectedDate.toISOString().slice(0, 16),
            endDateTime: selectedDate.toISOString().slice(0, 16),
            taskStatus: "",
            description: "",
            assignee: "",
            color: "purple",
        },
        mode: "onChange",
    });

    // Function to find Job Execution task ID
    const findJobExecutionTaskId = async (projectId: string) => {
        try {
            const response = await listProjectTasksSvc.mutateAsync({ ProjectId: projectId });
            const tasks = response || [];

            // Find the task with name "Job Execution"
            const jobExecutionTask = tasks.find(
                (task: any) => task.Name && task.Name.toLowerCase().includes("job execution")
            );

            if (jobExecutionTask) {
                setJobExecutionTaskId(jobExecutionTask.Id);
                return jobExecutionTask.Id;
            } else {
                console.warn("Job Execution task not found for project:", projectId);
                return null;
            }
        } catch (error) {
            console.error("Error fetching project tasks:", error);
            return null;
        }
    };

    // Reset form when selectedDate changes
    useEffect(() => {
        methods.reset();
    }, [selectedDate, methods]);

    // Watch form state changes and notify parent
    useEffect(() => {
        if (onFormStateChange) {
            onFormStateChange(methods.formState.isValid);
        }
    }, [methods.formState.isValid, onFormStateChange]);

    // Watch for project changes and find Job Execution task ID
    const watchedProjectId = methods.watch("projectId");
    useEffect(() => {
        if (watchedProjectId) {
            // Extract projectId from the object structure
            const projectId = typeof watchedProjectId === "object" ? watchedProjectId.value : watchedProjectId;
            findJobExecutionTaskId(projectId);
        } else {
            setJobExecutionTaskId(null);
        }
    }, [watchedProjectId]);

    // Handle form submission
    const handleSubmit = async (data: NewSubTaskInlineFormData, onSubmit: (data: any) => void) => {
        try {
            // If Job Execution task ID is found, create subtask in database
            if (jobExecutionTaskId && data.projectId) {
                // Extract projectId from the object structure
                const projectId = typeof data.projectId === "object" ? data.projectId.value : data.projectId;

                // Extract assignee from the object structure
                const assigneeId = data.assignee
                    ? typeof data.assignee === "object"
                        ? data.assignee.value
                        : data.assignee
                    : null;

                const subtaskParams = {
                    ProjectId: projectId,
                    ParentTaskId: jobExecutionTaskId,
                    Name: data.eventName,
                    Status: data.taskStatus,
                    SortOrder: 0,
                    Description: data.description || data.eventName,
                    ExecutionDate: moment(data.startDateTime).utc().format("YYYY-MM-DD HH:mm:ss"),
                    ...(assigneeId && { AssigneeId: assigneeId }),
                };

                // Create subtask in database
                const createdTask = await createTaskSvc.mutateAsync(subtaskParams);

                // If task was created successfully, add color as a tag
                if (createdTask && data.color) {
                    try {
                        const colorTag = {
                            EntityType: "task",
                            EntityId: createdTask.Id || createdTask.id, // Handle different response formats
                            Tags: {
                                Color: data.color, // Capitalize first letter
                            },
                        };
                        await assignTagsSvc.mutateAsync(colorTag);
                    } catch (tagError) {
                        console.warn("Failed to assign color tag:", tagError);
                        // Don't fail the entire operation if tagging fails
                    }
                }
            }

            // Always call the parent onSubmit for calendar event creation
            onSubmit(data);
            methods.reset();
        } catch (error) {
            console.error("Error creating subtask:", error);
            // Still call onSubmit for calendar event even if subtask creation fails
            onSubmit(data);
            methods.reset();
        }
    };

    return {
        methods,
        handleSubmit,
        jobExecutionTaskId,
        isLoading: createTaskSvc.isPending || assignTagsSvc.isPending || methods.formState.isSubmitting,
    };
}
